/**
 * Sandboxes v2 view (the list page's `?view=v2`): applications to claim from,
 * and the live sandboxes.
 *
 * @module features/sandboxes/components/v2/SandboxV2View
 */
import React from 'react';
import { EmptyState, Flex, LoadingOrbit, Page, PageHeader, Text } from '@chatsdet/ui';
import { Terminal } from '@phosphor-icons/react';
import { BRAND } from '../../../../brand';
import { useSandboxV2Applications } from '../../hooks/useSandboxesV2';
import { ApplicationsSection } from './ApplicationsSection';
import { LiveSandboxesSection } from './LiveSandboxesSection';

function EmptyApplications() {
  const { cliCommand: cli } = BRAND;
  return (
    <EmptyState
      icon={<Terminal size={20} />}
      title="No sandbox applications yet"
      description={
        <>
          Connect a repository with <code className="font-mono">{cli} sandbox connect</code>, or push an image with{' '}
          <code className="font-mono">{cli} sandbox push</code>. Applications appear here once they are set up.
        </>
      }
    />
  );
}

export function SandboxV2View({ switcher }: { switcher: React.ReactNode }) {
  const applicationsQuery = useSandboxV2Applications();
  const applications = applicationsQuery.data ?? [];

  return (
    <Page>
      <PageHeader title="Test Environments" actions={switcher} />
      {applicationsQuery.isLoading ? (
        <Flex justify="center" className="py-24">
          <LoadingOrbit size="lg" />
        </Flex>
      ) : applicationsQuery.isError ? (
        <Text color="muted">{applicationsQuery.error.message}</Text>
      ) : applications.length === 0 ? (
        <EmptyApplications />
      ) : (
        <Flex direction="column" gap="lg">
          <ApplicationsSection applications={applications} />
          <LiveSandboxesSection applications={applications} />
        </Flex>
      )}
    </Page>
  );
}
